/**
 * Vendas > Guru (spec 063, fase 2): o espelho das vendas na Guru visto e mexido pela equipe. Estado, o que falta la
 * (com sincronizar), os envios (com reenviar) e a chave de ligar e desligar com motivo.
 *
 * O servico da Guru nao tem endereco publico: o painel fala com ele pelo proprio host (`/guru/v1/admin/*`, repassado
 * pelo nginx do container e pelo vite em dev), com o mesmo token. Nenhuma regra aqui: o que pode reenviar, o resumo do
 * que falta e o que acontece ao desligar vem do servico.
 */
import { Aviso, Botao, CampoData, CampoSelecao, CampoTextoLongo, Carregando, Fatos, Pilha, POR_PAGINA, Secao, Segmentado, Selo, Tabela, Vazio, formatarDataHora, formatarDia, formatarReais, hojeEmSaoPaulo, type Tom } from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { erroDe, useApi } from "../api";
import { usePagina } from "../pagina";
import { DialogoDeAcao } from "./DialogoDeAcao";

/** Endereco no servico da Guru, pelo host do painel. Absoluto: caminho relativo a sessao manda pro backend. */
export const GURU = (caminho: string) => `${window.location.origin}/guru${caminho}`;

type Mudanca = { ligado: boolean; motivo: string; operador_id: string; voltaram: number; em: string };
type Estado = {
  espelho: { ligado: boolean; chave_ligada: boolean; credenciais: boolean; pendentes: number; falhas: number; desistidas: number; sem_envio: number; mais_antiga_min: number; ultima_enviada_em: string | null };
  ultima_mudanca: Mudanca | null;
  faltando: { vendas: number; valor_centavos: number; primeira: string | null; ultima: string | null; ate: string };
};
type Situacao = "ENVIADA" | "NA_FILA" | "TENTANDO" | "DESISTIU" | "SEM_ENVIO";
type Envio = { codigo: string; origem: "EVENTO" | "HISTORICO"; situacao: Situacao; cliente: string | null; produto: string | null; valor_centavos: number; pago_em: string; enviada_em: string | null; tentativas: number; ultimo_erro: string | null; estorno: string | null; pode_reenviar: boolean };

const SITUACAO: Record<Situacao, { rotulo: string; tom: Tom }> = {
  ENVIADA: { rotulo: "Enviada", tom: "sucesso" },
  NA_FILA: { rotulo: "Na fila", tom: "info" },
  TENTANDO: { rotulo: "Tentando de novo", tom: "alerta" },
  DESISTIU: { rotulo: "Parou de tentar", tom: "erro" },
  SEM_ENVIO: { rotulo: "Sem envio", tom: "alerta" },
};
const FILTROS = [
  { valor: "todas", rotulo: "Todas" },
  { valor: "faltando", rotulo: "Faltando na Guru" },
  { valor: "na_fila", rotulo: "Na fila" },
  { valor: "com_falha", rotulo: "Com falha" },
  { valor: "enviadas", rotulo: "Enviadas" },
] as const;
type Filtro = (typeof FILTROS)[number]["valor"];
const ESTORNO: Record<string, string> = { REEMBOLSO: "Reembolsada", REEMBOLSO_PARCIAL: "Reembolso parcial", CONTESTACAO: "Contestada" };

/** Embaixo da situacao: quando foi, ou quantas tentativas e o erro da ultima (cortado; o inteiro fica no title). */
function detalhe(v: Envio): string | null {
  if (v.situacao === "ENVIADA") return v.enviada_em ? `Em ${formatarDataHora(v.enviada_em)}` : null;
  if (!v.ultimo_erro) return null;
  const erro = v.ultimo_erro.length > 80 ? `${v.ultimo_erro.slice(0, 80)}...` : v.ultimo_erro;
  return `${v.tentativas} ${v.tentativas === 1 ? "tentativa" : "tentativas"}: ${erro}`;
}

const vendas = (n: number) => `${n} ${n === 1 ? "venda" : "vendas"}`;

/** "há 5 minutos", "há 3 horas", "há 2 dias". */
function haQuanto(iso: string): string {
  const min = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60_000));
  if (min < 1) return "agora há pouco";
  if (min < 60) return `há ${min} ${min === 1 ? "minuto" : "minutos"}`;
  const h = Math.floor(min / 60);
  if (h < 48) return `há ${h} ${h === 1 ? "hora" : "horas"}`;
  return `há ${Math.floor(h / 24)} dias`;
}

export function AbaGuru() {
  const { ler } = useApi();
  const estado = useQuery({ queryKey: ["guru", "estado"], queryFn: () => ler<Estado>(GURU("/v1/admin/estado")), refetchInterval: 30_000 });
  const nomes = useQuery({ queryKey: ["nomes-dos-administradores"], queryFn: () => ler<{ itens: { id: string; nome: string }[] }>("/v1/admin/administradores/nomes"), staleTime: 5 * 60_000 });
  const nome = (id: string) => nomes.data?.itens.find((a) => a.id === id)?.nome ?? "Administrador";
  const [feito, setFeito] = useState<string>();

  if (estado.isLoading) return <Carregando compacto texto="Buscando o estado da Guru" />;
  if (estado.isError) return <Aviso tipo="erro">{erroDe(estado.error)}</Aviso>;
  const e = estado.data!;
  return (
    <Pilha espaco={4}>
      <p className="mtc-texto-2" style={{ margin: 0 }}>Cada venda paga do checkout vai como registro para a Guru. Aqui você vê se está indo, manda o que ficou de fora e liga ou desliga o envio.</p>
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      <EstadoDoEspelho estado={e} nome={nome} aoFazer={setFeito} />
      <Faltando estado={e} aoFazer={setFeito} />
      <Envios chaveLigada={e.espelho.chave_ligada} aoFazer={setFeito} />
      <TrilhaDaChave nome={nome} />
    </Pilha>
  );
}

function EstadoDoEspelho({ estado: e, nome, aoFazer }: { estado: Estado; nome: (id: string) => string; aoFazer: (t: string) => void }) {
  const { postar } = useApi();
  const qc = useQueryClient();
  const [mudar, setMudar] = useState(false);
  const [motivo, setMotivo] = useState("");
  const s = e.espelho;
  const naFila = s.pendentes + s.falhas;
  const desligar = s.chave_ligada;
  const m = e.ultima_mudanca;
  return (
    <Secao
      rotulo="Espelho"
      titulo={<span className="mtc-linha" style={{ gap: "var(--e-2)" }}>Envio para a Guru <Selo tom={s.ligado ? "sucesso" : "alerta"}>{s.ligado ? "Ligado" : "Desligado"}</Selo></span>}
      acoes={<Botao tamanho="pequeno" variante={desligar ? "perigo" : "primario"} onClick={() => setMudar(true)}>{desligar ? "Desligar o envio" : "Ligar o envio"}</Botao>}
    >
      <Pilha espaco={3}>
        {!s.chave_ligada && m && <Aviso tipo="alerta">Desligado por {nome(m.operador_id)} em {formatarDataHora(m.em)}: {m.motivo}. As vendas desse período não vão para a Guru até alguém sincronizar.</Aviso>}
        {s.chave_ligada && !s.credenciais && <Aviso tipo="info">Este ambiente não tem as credenciais da Guru: nenhuma venda sai daqui, mesmo com o envio ligado.</Aviso>}
        <Fatos
          itens={[
            { rotulo: "Última venda enviada", valor: s.ultima_enviada_em ? haQuanto(s.ultima_enviada_em) : "Nenhuma ainda", detalhe: s.ultima_enviada_em ? formatarDataHora(s.ultima_enviada_em) : undefined },
            { rotulo: "Na fila", valor: vendas(naFila), detalhe: naFila && s.mais_antiga_min >= 5 ? `a mais antiga espera há ${s.mais_antiga_min} min` : undefined },
            { rotulo: "Tentando de novo", valor: vendas(s.falhas) },
            { rotulo: "Faltando na Guru", valor: vendas(e.faltando.vendas), detalhe: e.faltando.vendas ? formatarReais(e.faltando.valor_centavos) : undefined },
          ]}
        />
      </Pilha>
      <DialogoDeAcao
        aberto={mudar}
        aoFechar={() => setMudar(false)}
        titulo={desligar ? "Desligar o envio para a Guru" : "Ligar o envio para a Guru"}
        descricao={desligar
          ? `As vendas desse período não vão para a Guru até alguém sincronizar.${naFila ? ` ${vendas(naFila)} na fila ${naFila === 1 ? "volta" : "voltam"} para Faltando.` : ""}`
          : "Só as próximas vendas vão. O que ficou de fora enquanto estava desligado continua em Faltando, para você sincronizar."}
        botao={desligar ? "Desligar" : "Ligar"}
        variante={desligar ? "perigo" : "primario"}
        podeEnviar={motivo.trim().length >= 5}
        aoAbrir={() => setMotivo("")}
        aoEnviar={() => postar(GURU("/v1/admin/espelho"), { ligado: !desligar, motivo: motivo.trim() })}
        aoConcluir={() => { void qc.invalidateQueries({ queryKey: ["guru"] }); aoFazer(desligar ? "Envio para a Guru desligado." : "Envio para a Guru ligado."); }}
      >
        <CampoTextoLongo rotulo="Motivo" valor={motivo} aoMudar={setMotivo} maxLength={500} placeholder={desligar ? "Ex.: a Guru está fora do ar desde as 14h." : "Ex.: a Guru voltou, conferido no painel deles."} dica="Fica no histórico, com seu nome e a hora." />
      </DialogoDeAcao>
    </Secao>
  );
}

/** "Faltando na Guru" (secao 4, item 3): pago aqui e sem envio. Sincronizar manda de uma vez, com o resumo antes. */
function Faltando({ estado: e, aoFazer }: { estado: Estado; aoFazer: (t: string) => void }) {
  const { postar } = useApi();
  const qc = useQueryClient();
  const [confirmar, setConfirmar] = useState(false);
  const f = e.faltando;
  const periodo = f.primeira && f.ultima ? (formatarDia(f.primeira) === formatarDia(f.ultima) ? `pagas em ${formatarDia(f.primeira)}` : `pagas de ${formatarDia(f.primeira)} a ${formatarDia(f.ultima)}`) : "";
  const resumo = `${vendas(f.vendas)}, ${formatarReais(f.valor_centavos)}${periodo ? `, ${periodo}` : ""}`;
  return (
    <Secao rotulo="Conferência" titulo="Faltando na Guru" acoes={f.vendas > 0 ? <Botao tamanho="pequeno" variante="secundario" disabled={!e.espelho.chave_ligada} onClick={() => setConfirmar(true)}>Sincronizar</Botao> : undefined}>
      {f.vendas === 0 ? (
        <p className="mtc-texto-2" style={{ margin: 0 }}>Nada faltando: toda venda paga está na Guru ou na fila para ir.</p>
      ) : (
        <Pilha espaco={2}>
          <p style={{ margin: 0 }}><strong>{resumo}.</strong> Pagas aqui e que não foram para a Guru: chegaram com o envio desligado ou pararam de tentar.</p>
          {!e.espelho.chave_ligada && <p className="mtc-texto-2" style={{ margin: 0 }}>Ligue o envio antes de sincronizar.</p>}
        </Pilha>
      )}
      <DialogoDeAcao
        aberto={confirmar}
        aoFechar={() => setConfirmar(false)}
        titulo="Sincronizar com a Guru"
        descricao={`Vão para a Guru agora: ${resumo}. O que já foi não vai de novo.`}
        botao="Mandar para a Guru"
        aoEnviar={() => postar(GURU("/v1/admin/faltando/sincronizar"), { ate: f.ate, vendas: f.vendas })}
        aoConcluir={() => { void qc.invalidateQueries({ queryKey: ["guru"] }); aoFazer(`${vendas(f.vendas)} na fila para a Guru. Saem nos próximos minutos.`); }}
      />
    </Secao>
  );
}

/** Envios (secao 4, item 2): a lista por situacao e periodo do pagamento, com reenviar so o que falhou ou ficou sem envio. */
function Envios({ chaveLigada, aoFazer }: { chaveLigada: boolean; aoFazer: (t: string) => void }) {
  const { ler, postar } = useApi();
  const qc = useQueryClient();
  const [filtro, setFiltro] = useState<Filtro>("todas");
  const [periodo, setPeriodo] = useState<"todo" | "datas">("todo");
  const [de, setDe] = useState(() => `${hojeEmSaoPaulo().slice(0, 8)}01`);
  const [ate, setAte] = useState(() => hojeEmSaoPaulo());
  const [reenviar, setReenviar] = useState<Envio | null>(null);
  const q = new URLSearchParams({ situacao: filtro, ...(periodo === "datas" && de ? { desde: de } : {}), ...(periodo === "datas" && ate ? { ate } : {}) }).toString();
  const pag = usePagina("pagina", [q]);
  const lista = useQuery({ queryKey: ["guru", "envios", q, pag.pagina], queryFn: () => ler<{ itens: Envio[]; total: number; pagina: number }>(GURU(`/v1/admin/envios?${q}&pagina=${pag.pagina}&por_pagina=${POR_PAGINA}`)), placeholderData: (anterior) => anterior, refetchInterval: 30_000 });
  return (
    <Secao rotulo="Registro" titulo="Envios">
      <Pilha espaco={3}>
        <div className="mtc-linha" style={{ gap: "var(--e-3)", flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ flex: "0 1 220px" }}><CampoSelecao rotulo="Situação" valor={filtro} aoMudar={(v) => setFiltro((v || "todas") as Filtro)} opcoes={FILTROS.map((f) => ({ valor: f.valor, rotulo: f.rotulo }))} /></div>
          <Segmentado rotulo="Pago em" nome="periodo-da-guru" valor={periodo} aoMudar={setPeriodo} opcoes={[{ valor: "todo", rotulo: "Todo o período" }, { valor: "datas", rotulo: "Escolher datas" }]} />
          {periodo === "datas" && <div style={{ flex: "0 1 180px" }}><CampoData rotulo="De" valor={de} aoMudar={setDe} /></div>}
          {periodo === "datas" && <div style={{ flex: "0 1 180px" }}><CampoData rotulo="Até" valor={ate} aoMudar={setAte} /></div>}
        </div>
        {lista.isLoading ? <Carregando compacto texto="Buscando os envios" /> : lista.isError ? <Aviso tipo="erro">{erroDe(lista.error)}</Aviso> : (
          <Tabela
            linhas={lista.data?.itens ?? []}
            paginacao={{ pagina: lista.data?.pagina ?? pag.pagina, aoMudar: pag.aoMudar, total: lista.data?.total ?? 0 }}
            chave={(v) => v.codigo}
            vazio={filtro === "todas" && periodo === "todo" ? <Vazio titulo="Nenhuma venda ainda">As vendas pagas pelo checkout aparecem aqui assim que chegam.</Vazio> : <Vazio titulo="Nada com esse filtro">Troque a situação ou as datas.</Vazio>}
            colunas={[
              { chave: "quando", rotulo: "Pago em", celula: (v) => <span className="mtc-mono">{formatarDataHora(v.pago_em)}</span> },
              // O pedido vai embaixo do cliente e o detalhe embaixo da situacao: com sete colunas a tabela nao cabia em 1280 px e
              // cortava o Reenviar (print do e2e, 26/10).
              { chave: "cliente", rotulo: "Cliente", celula: (v) => <>{v.origem === "HISTORICO" ? <span className="mtc-texto-2">Do histórico</span> : <strong>{v.cliente}</strong>}<span className="mtc-tabela__apoio">{v.origem === "HISTORICO" ? "Enviada pelo sistema anterior" : v.produto}</span><span className="mtc-tabela__apoio mtc-mono">{v.codigo}</span></> },
              { chave: "valor", rotulo: "Valor", alinhar: "direita" as const, celula: (v) => <span className="mtc-mono">{formatarReais(v.valor_centavos)}</span> },
              { chave: "situacao", rotulo: "Situação", celula: (v) => <><Selo tom={SITUACAO[v.situacao].tom}>{SITUACAO[v.situacao].rotulo}</Selo>{v.estorno && <span className="mtc-tabela__apoio">{ESTORNO[v.estorno] ?? v.estorno}</span>}{detalhe(v) && <span className="mtc-tabela__apoio" title={v.ultimo_erro ?? undefined}>{detalhe(v)}</span>}</> },
              { chave: "acao", rotulo: "", celula: (v) => (v.pode_reenviar ? <Botao variante="fantasma" tamanho="pequeno" disabled={!chaveLigada} onClick={() => setReenviar(v)}>Reenviar</Botao> : null) },
            ]}
          />
        )}
      </Pilha>
      <DialogoDeAcao
        aberto={Boolean(reenviar)}
        aoFechar={() => setReenviar(null)}
        titulo="Reenviar para a Guru"
        descricao={reenviar ? `A venda ${reenviar.codigo}${reenviar.cliente ? ` de ${reenviar.cliente}` : ""} (${formatarReais(reenviar.valor_centavos)}) volta para a fila e sai nos próximos minutos.` : undefined}
        botao="Reenviar"
        aoEnviar={() => postar(GURU(`/v1/admin/envios/${reenviar!.codigo}/reenviar`))}
        aoConcluir={() => { void qc.invalidateQueries({ queryKey: ["guru"] }); aoFazer(`Venda ${reenviar?.codigo ?? ""} na fila para a Guru.`); }}
      />
    </Secao>
  );
}

/** Quem ligou e desligou, quando e por que (secao 4, item 4). */
function TrilhaDaChave({ nome }: { nome: (id: string) => string }) {
  const { ler } = useApi();
  const q = useQuery({ queryKey: ["guru", "mudancas"], queryFn: () => ler<{ itens: Mudanca[] }>(GURU("/v1/admin/espelho/mudancas")) });
  if (q.isLoading || !q.data?.itens.length) return null;
  return (
    <Secao rotulo="Histórico" titulo="Quem ligou e desligou">
      <Tabela
        linhas={q.data.itens}
        chave={(m) => m.em}
        colunas={[
          { chave: "quando", rotulo: "Quando", celula: (m) => <span className="mtc-mono">{formatarDataHora(m.em)}</span> },
          { chave: "acao", rotulo: "O que", celula: (m) => <><Selo tom={m.ligado ? "sucesso" : "alerta"}>{m.ligado ? "Ligou" : "Desligou"}</Selo>{m.voltaram > 0 && <span className="mtc-tabela__apoio">{vendas(m.voltaram)} da fila foram para Faltando</span>}</> },
          { chave: "quem", rotulo: "Quem", celula: (m) => nome(m.operador_id) },
          { chave: "motivo", rotulo: "Motivo", celula: (m) => m.motivo },
        ]}
      />
    </Secao>
  );
}
